/**
 * Editor surface for managing an article's embedded saved queries.
 *
 * Mounted inside `ArticleEditor` between the markdown editor and the
 * change-note card. Only renders for an existing article (not yet
 * saved → no article id to attach embeds to).
 *
 * Each row carries:
 *   - The saved query's name as a faint fallback caption
 *   - An inline-editable title override (commits on blur if dirty)
 *   - ▲ / ▼ reorder buttons (disabled at list ends)
 *   - Remove button (with a confirm)
 *
 * Mutations save immediately and refetch on success — no batching, no
 * "save embeds" button. This is consistent with the design goal that
 * embed CRUD is independent of the article body's optimistic-lock
 * lifecycle (mutations don't bump the article's `current_version`).
 *
 * Errors surface inline on the affected row + a top-level Alert. The
 * list refetches from the backend after every successful mutation so
 * the local UI is always sourced from the canonical server state.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { useLeagueApi } from "../../../../data/LeagueEnvironmentProvider";
import {
  ResearchEmbeddedQuery,
  SearchableSavedQuery,
} from "../../../../models/Research";
import QueryPickerDialog from "./QueryPickerDialog";

export interface EmbeddedQueriesListProps {
  slug: string;
}

export default function EmbeddedQueriesList({
  slug,
}: EmbeddedQueriesListProps) {
  const api = useLeagueApi();
  const [embeds, setEmbeds] = useState<ResearchEmbeddedQuery[]>([]);
  // `initialLoading` is true ONLY until the first successful fetch.
  // Subsequent refetches (after add / reorder / title / delete) leave
  // the existing list rendered while the network roundtrip happens,
  // so the optimistic local update stays visible instead of the list
  // collapsing into a "Loading…" placeholder for a beat.
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const refetch = React.useCallback(() => {
    let cancelled = false;
    api
      .getResearchEmbeddedQueries(slug)
      .then((resp) => {
        if (cancelled) return;
        setEmbeds(resp.data.embedded_queries ?? []);
        setInitialLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load embedded queries";
        setError(msg);
        setInitialLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, slug]);

  useEffect(() => {
    return refetch();
  }, [refetch]);

  const handlePick = async (sq: SearchableSavedQuery) => {
    setPickerOpen(false);
    setBusy(true);
    setError(null);
    try {
      await api.addResearchEmbeddedQuery(slug, {
        saved_query_id: sq.id,
      });
      refetch();
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to add embedded query",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleTitleSave = async (
    embedId: string,
    titleOverride: string | null,
  ) => {
    // Optimistic: write the new title locally so the row doesn't
    // briefly snap back to the pre-edit value during the refetch.
    setEmbeds((prev) =>
      prev.map((e) =>
        e.id === embedId ? { ...e, title_override: titleOverride } : e,
      ),
    );
    setBusy(true);
    setError(null);
    try {
      await api.updateResearchEmbeddedQuery(slug, embedId, {
        title_override: titleOverride,
      });
      refetch();
    } catch (err: any) {
      refetch(); // revert to server truth on failure
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to update title",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleMove = async (embedId: string, direction: -1 | 1) => {
    const idx = embeds.findIndex((e) => e.id === embedId);
    if (idx < 0) return;
    const target = idx + direction;
    if (target < 0 || target >= embeds.length) return;
    // Optimistic local swap.
    const next = embeds.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    setEmbeds(next);
    setBusy(true);
    setError(null);
    try {
      await api.reorderResearchEmbeddedQueries(slug, {
        embed_ids: next.map((e) => e.id),
      });
      refetch();
    } catch (err: any) {
      // Revert and surface.
      refetch();
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to reorder embedded queries",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (embedId: string) => {
    if (
      !window.confirm(
        "Remove this embedded query? The underlying saved query is unaffected.",
      )
    ) {
      return;
    }
    // Optimistic local removal — the row vanishes immediately rather
    // than disappearing partway into a "Loading…" beat.
    setEmbeds((prev) => prev.filter((e) => e.id !== embedId));
    setBusy(true);
    setError(null);
    try {
      await api.deleteResearchEmbeddedQuery(slug, embedId);
      refetch();
    } catch (err: any) {
      refetch(); // restore the row from server on failure
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to delete embedded query",
      );
    } finally {
      setBusy(false);
    }
  };

  const alreadyEmbeddedIds = React.useMemo(
    () => new Set(embeds.map((e) => e.saved_query_id)),
    [embeds],
  );

  return (
    <Box
      sx={{
        backgroundColor: "#fafafa",
        border: "1px solid #e6e6e6",
        borderRadius: "4px",
        p: 2.5,
        mb: 3,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          Embedded queries
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setPickerOpen(true)}
          disabled={busy}
          sx={{ textTransform: "none" }}
        >
          Add query
        </Button>
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1.5, fontSize: "0.85rem" }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {initialLoading ? (
        <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
          Loading…
        </Typography>
      ) : embeds.length === 0 ? (
        <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
          No embedded queries yet. Click <strong>Add query</strong> to
          pick one of your public saved queries.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {embeds.map((embed, idx) => (
            <EmbeddedQueryRow
              key={embed.id}
              embed={embed}
              isFirst={idx === 0}
              isLast={idx === embeds.length - 1}
              disabled={busy}
              onMoveUp={() => handleMove(embed.id, -1)}
              onMoveDown={() => handleMove(embed.id, 1)}
              onDelete={() => handleDelete(embed.id)}
              onSaveTitle={(t) => handleTitleSave(embed.id, t)}
            />
          ))}
        </Stack>
      )}

      <QueryPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handlePick}
        alreadyEmbeddedIds={alreadyEmbeddedIds}
      />
    </Box>
  );
}

function EmbeddedQueryRow({
  embed,
  isFirst,
  isLast,
  disabled,
  onMoveUp,
  onMoveDown,
  onDelete,
  onSaveTitle,
}: {
  embed: ResearchEmbeddedQuery;
  isFirst: boolean;
  isLast: boolean;
  disabled: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onSaveTitle: (titleOverride: string | null) => void;
}) {
  // Locally controlled title-override field so typing doesn't fire a
  // PUT on every keystroke. Commit happens on blur or Enter; the
  // backend stores null for empty strings, so we normalize here too.
  const [draftTitle, setDraftTitle] = useState<string>(
    embed.title_override ?? "",
  );

  // If a refetch lands a fresh embed (e.g. after move), sync the field.
  useEffect(() => {
    setDraftTitle(embed.title_override ?? "");
  }, [embed.title_override]);

  const commitTitle = () => {
    const normalized = draftTitle.trim() || null;
    if (normalized === (embed.title_override ?? null)) return;
    onSaveTitle(normalized);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #e6e6e6",
        borderRadius: "3px",
        p: 1.25,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={embed.name}
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                (e.target as HTMLInputElement).blur();
              }
            }}
            disabled={disabled}
            helperText={
              draftTitle.trim()
                ? `Override · falls back to "${embed.name}"`
                : `Using saved query name: "${embed.name}"`
            }
            FormHelperTextProps={{
              sx: { fontSize: "0.72rem", color: "text.secondary", ml: 0 },
            }}
          />
        </Box>
        <Stack
          direction="row"
          spacing={0.25}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          <Tooltip title="Move up" arrow>
            <span>
              <IconButton
                size="small"
                onClick={onMoveUp}
                disabled={isFirst || disabled}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down" arrow>
            <span>
              <IconButton
                size="small"
                onClick={onMoveDown}
                disabled={isLast || disabled}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove" arrow>
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={disabled}
                sx={{ color: "#CF0A2C" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
}
